import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";

// Voyages are active/archived only (no 'suggested').
const STATE_ACTIONS = [
  { value: "active", label: "Set as Active", Icon: PlayCircleOutlineOutlinedIcon },
  { value: "archived", label: "Archive", Icon: ArchiveOutlinedIcon },
];

export default function VoyageActionsMenu({
  voyageActionAnchorEl,
  voyageActionTarget,
  onCloseVoyageActionMenu,
  onSetVoyageState,
  voyageDeleteOpen,
  voyageDeleteCandidate,
  onCloseVoyageDeleteDialog,
  onVoyageDeleteConfirm,
  onOpenVoyageDeleteFromAction,
}) {
  const voyage = voyageActionTarget?.voyage;
  // Offer every state except the current one.
  const voyageState =
    voyage?.state ?? (voyage?.active === false ? "archived" : "active");

  const runAction = (nextState) => {
    const target = voyageActionTarget;
    onCloseVoyageActionMenu();
    if (!target) return;
    onSetVoyageState?.(target, nextState);
  };

  return (
    <>
      <Dialog
        open={Boolean(voyageDeleteOpen)}
        onClose={onCloseVoyageDeleteDialog}
        slotProps={{
          transition: {
            onExited: () => onOpenVoyageDeleteFromAction?.(null),
          },
          paper: {
            sx: {
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 24,
            },
          },
        }}
        disableRestoreFocus
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: "#004f7d", pb: 0.5 }}>
          Delete Voyage
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ fontSize: 14 }}>
            Delete voyage{" "}
            <strong>
              {voyageDeleteCandidate?.voyage?.name ??
                (voyageDeleteCandidate?.voyage?.id != null
                  ? `Voyage ${voyageDeleteCandidate.voyage.id}`
                  : "")}
            </strong>
            ?
          </DialogContentText>
          <DialogContentText
            sx={{ mt: 1, fontSize: 13, color: "text.secondary" }}
          >
            This will also remove all routes and waypoints of this voyage.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.25, pt: 0.75 }}>
          <Button
            variant="outlined"
            onClick={onCloseVoyageDeleteDialog}
            sx={{
              textTransform: "none",
              minWidth: 86,
              borderColor: "rgba(0,79,125,0.35)",
              color: "#004f7d",
              "&:hover": {
                borderColor: "#004f7d",
                backgroundColor: "rgba(0,79,125,0.06)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={onVoyageDeleteConfirm}
            sx={{ textTransform: "none", minWidth: 86 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={voyageActionAnchorEl}
        open={Boolean(voyageActionAnchorEl)}
        onClose={onCloseVoyageActionMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {STATE_ACTIONS.filter((a) => a.value !== voyageState).map(
          ({ value, label, Icon }) => (
            <MenuItem
              key={value}
              sx={{ fontSize: 13, color: "#004f7d" }}
              onClick={() => runAction(value)}
            >
              <ListItemIcon sx={{ minWidth: 28, color: "#004f7d" }}>
                <Icon sx={{ fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={label}
                slotProps={{ primary: { sx: { fontSize: 13 } } }}
              />
            </MenuItem>
          ),
        )}
        <MenuItem
          sx={{ fontSize: 13, color: "error.main" }}
          onClick={() => {
            const target = voyageActionTarget;
            onCloseVoyageActionMenu();
            if (!target) return;
            onOpenVoyageDeleteFromAction?.(target);
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: "error.main" }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 13 } } }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
